import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { handleFetchPredictionDatasets } from "../../modules/datasetModule.ts";
import { handleBatchPrediction, GetBatchPredictions } from "../../modules/predictorModule.ts";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { grey } from "@mui/material/colors";
import Button from "../../components/common/Button.tsx";
import RecentPredictions from "./RecentPredictions.tsx";
import {toast, ToastContainer} from 'react-toastify';import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { CardBody, Col, Row } from "reactstrap";
import { LabelText } from "../../components/common/Labeltext.tsx";
import SelectField from "../../components/common/SelectField.tsx";
import { useSSE } from "../../context/SSEContext.tsx";

interface Dataset {
  id: number;
  tenant_id: string;
  dataset_name: string;
  file_name: string;
  total_fields: number;
  total_records: number;
  created_by: number;
  template_id: number;
  error_report: string | null;
  created_at: string;
  modified_at: string;
}

const BatchPrediction = () => {
  const navigate = useNavigate();
  //const toast = useToast();

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recentPredictions, setRecentPredictions] = useState([])
  const [datasetLoading, setDatasetLoading] = useState(false);
  const location = useLocation();
  const { subscribeJob } = useSSE();
  

  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    const getDatasets = async () => {
      try {
        setDatasetLoading(true);
        const data = await handleFetchPredictionDatasets(location.state.predictor.dataset_id);
        setDatasets(data);
        setDatasetLoading(false);
      } catch (error) {
        console.error("Failed to fetch datasets", error);
        toast.error("Failed to fetch datasets");
      } finally {
        setDatasetLoading(false);
      }
    };
    getDatasets();
  }, []);

  const handleSubmit = async () => {
    if (!selectedDataset) {
      toast.error("Please select a dataset");
      return;
    }
    if (!id) {
      toast.error("Invalid train ID");
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        train_id: parseInt(id),
        dataset_id: selectedDataset.id,
      };

      const response = await handleBatchPrediction(payload);
      subscribeJob("batch_prediction",response?.data?.dataset_id)
      if (response) {
        navigate("/TrainingModal", { state: { BatchData: response } });
      }
      toast.success("Batch prediction is in Progress!");
    } catch (error) {
      const errorMessage = error?.response?.data?.detail || "Failed to initiate batch prediction";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    const getBatchPrediction = async () => {
      if (!id) {
        toast.error("Invalid train ID");
        return;
      }

      try {
        setIsLoading(true);
        const response = await GetBatchPredictions(id);
        setRecentPredictions(response.filter(pred => pred.prediction_type === "Batch"));
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || "Failed to fetch recent batch prediction";
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    getBatchPrediction()
  }, [])

  return (
    <>
      <BreadCrumb moduleName={"Training"} pageName={"Batch Prediction"} viewName={location?.state?.predictor?.predictor_name} />
      <ToastContainer position="top-center" />
      <div className="listingjs-table " id="customerList">
        <div className="live-preview">
          <div className="align-items-center d-flex mb-3 ">
            <h4 className="card-title mb-0 flex-grow-1">Batch Prediction</h4>
          </div>
          <div className="border table-border p-3">
            <div className=''>
              <CardBody>
                <Row className="gy-2 pt-1 pb-2 ">
                  <Col lg={12}>
                    <LabelText
                      htmlFor={"Dataset"}
                      className={"text-danger"}
                      displayText={"Dataset Selection"}
                      important={"*"}
                    />
                    <LabelText
                      htmlFor={"DatasetPlaceholder"}
                      classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                      displayText={`Select a dataset`}
                    />
                    <SelectField
                      className={""}
                      value={selectedDataset}
                      placeholder={" "}
                      handleChange={(selectedOption) => {
                        setSelectedDataset(selectedOption)
                      }}
                      showLoadingIndicator={datasetLoading}
                      options={datasets.map((dataset) => ({ label: dataset.dataset_name, value: dataset.dataset_name }))}
                      isRVStyle={true}
                    />
                  </Col>
                </Row>
                <Row lg={6} className="justify-content-center mt-4">
                  <div className="live-preview">
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        onClick={handleSubmit}
                        type="submit"
                        className="btn-label rv-primary-btn rounded-1 btn btn-secondary d-flex align-items-center"
                        style={{ height: 'auto', minHeight: 'fit-content' }}
                      >
                        <i className="ri-play-fill label-icon align-middle fs-16 me-1"></i>
                        <span className="ms-2 me-1 fw-bold d-inline-flex align-items-center gap-2">
                          {isLoading ? (
                            <>
                              <div className="spinner-border spinner-border-sm text-light" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <span>Please wait</span>
                            </>
                          ) : (
                            "Run Prediction"
                          )}
                        </span>
                      </button>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </div>
          </div>
          <div className="align-items-center d-flex mb-3 mt-4">
            <h4 className="card-title mb-0 flex-grow-1">Recent Batch Predictions</h4>
          </div>
          <RecentPredictions predictions={recentPredictions} predictor={location.state.predictor} />
        </div>
      </div>
    </>
  );
};

export default BatchPrediction;
